import { useHistory } from "react-router-dom";
import { Button, Flex, ModalV2, Text, toast } from "@jsq/ds/core";
import { globalEnv } from "~/global-env";
import { useRemoveProfileFromCaseMutation } from "~/graphql/supergraphHooks";
import { ProfileNameAndId } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/types";
import { SPACING } from "~/styles/variables";

interface Props {
  profileId: number;
  isOrHasPaymentBeneficiary: boolean;
  profileName: string | undefined;
  caseId: number;
  isOpen: boolean;
  descendants: ProfileNameAndId[];
}

export const DeleteProfileModal = ({
  profileId,
  isOrHasPaymentBeneficiary,
  profileName,
  caseId,
  isOpen,
  descendants,
}: Props) => {
  const history = useHistory();

  const [removeProfileFromCase, { loading }] = useRemoveProfileFromCaseMutation({
    refetchQueries: [
      "GetProfilesForCase",
      "GetComplianceCase",
      "GetCaseInvestingProfileRiskRating",
    ],
    awaitRefetchQueries: true,
    onError: (e) => {
      toast.error(e.message);
      throw e;
    },
  });

  // This is what happens when the user clicks the actual final delete confirmation
  const deleteProfile = async () => {
    try {
      await removeProfileFromCase({
        variables: { caseId, profileId },
      });
      toast.success("Profile(s) deleted");
      closeModal();
    } catch {
      // do nothing
    }
  };

  const closeModal = () => {
    history.push(`${globalEnv.complianceRoute}/case/${caseId}/profiles`);
  };

  const listDescendants = () => {
    return (
      <Text asSpan variant="regular-md">
        <ul>
          {descendants.map((d) => (
            <li key={d.id}>{d.name}</li>
          ))}
        </ul>
      </Text>
    );
  };

  return (
    <ModalV2
      show={isOpen}
      onHide={closeModal}
      isCentered={true}
      backdrop={true}
      title="Warning"
    >
      <Flex flexDirection="column" css={{ marginBottom: SPACING.MEDIUM }}>
        <Text marginBottom="md">
          {isOrHasPaymentBeneficiary ? (
            `Cannot delete ${profileName} because the profile or one or more of its owners and/or controllers is a payment beneficiary.`
          ) : descendants.length > 0 ? (
            <>
              <Text marginBottom="md">
                Deleting {profileName} will also delete its owners and controllers:
              </Text>
              {listDescendants()}
            </>
          ) : (
            `Are you sure you want to delete ${profileName}?`
          )}
        </Text>
      </Flex>
      <Flex justifyContent="flex-end" css={{ marginBottom: SPACING.MEDIUM }}>
        <Button onClick={closeModal} variant="neutral" css={{ marginRight: SPACING.MEDIUM }}>
          Cancel
        </Button>
        <Button
          isLoading={loading}
          onClick={() => void deleteProfile()}
          variant="danger"
          css={{ marginRight: SPACING.MEDIUM }}
          disabled={isOrHasPaymentBeneficiary}
        >
          Delete
        </Button>
      </Flex>
    </ModalV2>
  );
};
